import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { COLORS } from '../../constants/theme';
import { formatNumber, formatDelta } from '../../utils/format';
import { GlanceCard } from '../shell/GlanceCard';
import { MetricRow } from '../shell/MetricRow';
import { ratioWidthPercent } from '../shell/HubCard';
import { AccordionCard } from './AccordionCard';
import {
  computeArmy,
  computeUnfilledJobs,
  getEmploymentStatus,
  getCapacityStatus,
  getNetChangeColor,
  formatNetValue,
  NET_CHANGE_GROUPS,
} from '../../utils/economyStats';
import {
  buildGoldRows,
  buildFoodRows,
  buildRunesRows,
  buildModifiers,
  SOURCE_COLORS,
  type FlowRow,
  type Modifier,
  type ModifierValue,
} from '../../utils/economyFlow';
import type { EconomyShellProps } from '../../pages/Economy';

type TrendPeriod = 'yesterday' | 'month' | 'lastMonth';
const TREND_TABS: readonly { key: TrendPeriod; label: string }[] = [
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'month', label: 'This Month' },
  { key: 'lastMonth', label: 'Last Month' },
];

type FlowKey = 'gold' | 'food' | 'runes';

const RESOURCE_LABELS: Record<'gold' | 'food' | 'runes' | 'other', string> = {
  gold: 'Gold',
  food: 'Food',
  runes: 'Runes',
  other: 'Other',
};

function CardSeparator() {
  return <div className="my-1" style={{ borderTop: `1px solid ${COLORS.border}` }} />;
}

/**
 * One waterfall row for the Flow Breakdown accordions — label + amount on one line, the
 * calculation string as a smaller sub-line beneath, indented modifier rows show a colored
 * source-dot + percentage badge in place of an amount, subtotal/total rows visually distinguished
 * via font weight and value color (Components §2).
 */
function MobileFlowRow({ row }: { row: FlowRow }) {
  const isNegative = row.value < 0;
  const emphasized = row.isTotal || row.isSubtotal;
  const valueColor = row.isTotal
    ? (row.value >= 0 ? COLORS.positive : COLORS.negative)
    : row.isSubtotal
      ? COLORS.text.primary
      : COLORS.text.secondary;

  return (
    <div className="flex flex-col gap-0.5 py-1" style={{ opacity: row.dimmed ? 0.4 : 1 }}>
      <div className="flex justify-between items-baseline gap-2">
        <span
          className="text-sm flex items-center gap-1.5"
          style={{
            color: row.indent ? SOURCE_COLORS[row.source] : COLORS.text.primary,
            fontWeight: emphasized ? 600 : 400,
          }}
        >
          {row.indent && (
            <span
              aria-hidden="true"
              className="inline-block w-1.5 h-1.5 rounded-full flex-shrink-0"
              style={{ backgroundColor: SOURCE_COLORS[row.source] }}
            />
          )}
          {row.label}
        </span>
        <span className="text-sm font-mono flex-shrink-0" style={{ color: valueColor, fontWeight: emphasized ? 600 : 400 }}>
          {row.indent
            ? row.unit
            : emphasized
              ? `${formatNumber(Math.round(row.value))} ${row.unit}`
              : `${isNegative ? '-' : ''}${formatNumber(Math.abs(Math.round(row.value)))} ${row.unit}`}
        </span>
      </div>
      {!row.indent && row.calculation && (
        <span className="text-[11px]" style={{ color: COLORS.text.tertiary }}>{row.calculation}</span>
      )}
    </div>
  );
}

function ModifierChip({ resourceKey, value }: { resourceKey: 'gold' | 'food' | 'runes' | 'other'; value: ModifierValue }) {
  const color = value.negative ? COLORS.negative : COLORS.positive;
  return (
    <span
      className="text-[11px] font-mono px-1.5 py-0.5 rounded"
      style={{ color, border: `1px solid ${color}` }}
    >
      {/* sr-only: redundant with (not a replacement for) color, closes WCAG 1.4.1 (Components §2) */}
      <span className="sr-only">{value.negative ? 'Decrease: ' : 'Increase: '}</span>
      {RESOURCE_LABELS[resourceKey]} {value.text}
    </span>
  );
}

function ModifierRow({ mod, isLast }: { mod: Modifier; isLast: boolean }) {
  const fields: Array<[keyof typeof RESOURCE_LABELS, ModifierValue | null]> = [
    ['gold', mod.gold],
    ['food', mod.food],
    ['runes', mod.runes],
    ['other', mod.other],
  ];
  return (
    <div className="flex flex-col gap-1.5 py-2" style={{ borderBottom: isLast ? undefined : `1px solid ${COLORS.border}` }}>
      <div className="flex items-center gap-1.5 text-sm" style={{ color: COLORS.text.primary }}>
        <span aria-hidden="true" className="inline-block w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: SOURCE_COLORS[mod.source] }} />
        {mod.name}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {fields
          .filter((entry): entry is [keyof typeof RESOURCE_LABELS, ModifierValue] => entry[1] !== null)
          .map(([key, value]) => <ModifierChip key={key} resourceKey={key} value={value} />)}
      </div>
    </div>
  );
}

/**
 * SF-164: mobile card-based content for the merged Economy page (Council's `EconomyTab` +
 * `ResourceFlowsTab`, dissolved). Read-only — no submit/mutation path.
 */
export function MobileEconomyShell({ province: p }: EconomyShellProps) {
  const location = useLocation();
  const h1Ref = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    h1Ref.current?.focus();
  }, [location.pathname]);

  const [trendPeriod, setTrendPeriod] = useState<TrendPeriod>('yesterday');
  const [expandedFlow, setExpandedFlow] = useState<FlowKey | null>('gold');

  const gold = useMemo(() => buildGoldRows(p), [p]);
  const food = useMemo(() => buildFoodRows(p), [p]);
  const runes = useMemo(() => buildRunesRows(p), [p]);
  const modifiers = useMemo(() => buildModifiers(p), [p]);

  const army = computeArmy(p.population);
  const emp = p.economy.employment;
  const unfilledJobs = computeUnfilledJobs(emp);
  const empStatus = getEmploymentStatus(emp.employmentRate);
  const capacityStatus = getCapacityStatus(p.population.capacityUnitCount ?? NaN, p.population.maxPopulation);
  const capacityBarWidth = capacityStatus
    ? ratioWidthPercent(p.population.capacityUnitCount as number, p.population.maxPopulation as number)
    : 0;

  const tc = p.economy.lastTickChanges;
  const curr = p.economy.currentMonth;
  const prev = p.economy.previousMonth;

  const salutation = p.metadata.ruler.gender === 'FEMALE' ? 'Lady' : 'Sir';
  const rulerName = p.metadata.ruler.name;

  return (
    <div className="space-y-3">
      <h1 ref={h1Ref} tabIndex={-1} className="text-lg font-bold text-game-50 outline-none">
        Economy
      </h1>
      <p className="text-sm -mt-1" style={{ color: COLORS.text.tertiary }}>
        {salutation} {rulerName}, here's how {p.metadata.name}'s economy is doing today.
      </p>

      {/* ── Population ────────────────────────────────────────── */}
      <GlanceCard title="Population">
        <MetricRow label="Peasants" value={formatNumber(p.population.peasants)} unit="" weight="secondary" />
        <MetricRow label="Army" value={formatNumber(army)} unit="" weight="secondary" />
        <MetricRow label="Thieves" value={formatNumber(p.population.thieves)} unit="" weight="secondary" />
        <MetricRow label="Wizards" value={formatNumber(p.population.wizards)} unit="" weight="secondary" />
        <CardSeparator />
        <MetricRow label="Total" value={formatNumber(p.population.total)} unit="" weight="secondary" />
        <MetricRow
          label="Max Population"
          value={p.population.maxPopulation != null ? formatNumber(p.population.maxPopulation) : '—'}
          unit=""
          weight="secondary"
        />
        {capacityStatus && (
          <div
            aria-hidden="true"
            style={{ height: 4, borderRadius: 2, overflow: 'hidden', background: COLORS.cardAlt, marginTop: 2 }}
          >
            <div style={{ height: '100%', borderRadius: 2, width: `${capacityBarWidth}%`, backgroundColor: capacityStatus.color }} />
          </div>
        )}
        <MetricRow
          label="Capacity"
          value={capacityStatus ? Math.round(((p.population.capacityUnitCount as number) / (p.population.maxPopulation as number)) * 100) : '—'}
          unit="%"
          weight="secondary"
          valueColor={capacityStatus?.color}
        />
        {capacityStatus && (
          <div className="text-[11px] -mt-1.5" style={{ color: capacityStatus.color }}>
            {capacityStatus.label}
          </div>
        )}
      </GlanceCard>

      {/* ── Employment ────────────────────────────────────────── */}
      <GlanceCard title="Employment">
        <MetricRow label="Jobs" value={formatNumber(emp.totalJobs)} unit="" weight="secondary" />
        <MetricRow label="Unemployed" value={formatNumber(emp.unemployed)} unit="" weight="secondary" />
        <MetricRow label="Unfilled Jobs" value={formatNumber(unfilledJobs)} unit="" weight="secondary" />
        <MetricRow
          label="Employment"
          value={empStatus ? Math.round(emp.employmentRate * 100) : '—'}
          unit="%"
          weight="secondary"
          valueColor={empStatus?.color}
        />
        {empStatus && (
          <div className="text-[11px] -mt-1.5" style={{ color: empStatus.color }}>
            {empStatus.label}
          </div>
        )}
      </GlanceCard>

      {/* ── Daily Net Flow ────────────────────────────────────── */}
      <GlanceCard title="Daily Net Flow">
        <MetricRow label="Net Gold" value={formatDelta(Math.round(gold.net))} unit="gc/day" weight="secondary" valueColor={gold.net >= 0 ? COLORS.positive : COLORS.negative} />
        <MetricRow label="Net Food" value={formatDelta(Math.round(food.net))} unit="bushels/day" weight="secondary" valueColor={food.net >= 0 ? COLORS.positive : COLORS.negative} />
        <MetricRow label="Net Runes" value={formatDelta(Math.round(runes.net))} unit="runes/day" weight="secondary" valueColor={runes.net >= 0 ? COLORS.positive : COLORS.negative} />
        <CardSeparator />
        <MetricRow label="Tax Rate" value={Math.round(p.economy.taxRate)} unit="%" weight="secondary" />
        <MetricRow label="Building Efficiency" value={(p.economy.buildingEfficiency * 100).toFixed(1)} unit="%" weight="secondary" />
      </GlanceCard>

      {/* ── Trends ────────────────────────────────────────────── */}
      <GlanceCard title="Trends">
        <div className="flex border-b overflow-x-auto -mt-1" style={{ borderColor: COLORS.border }} role="tablist" aria-label="Trends period">
          {TREND_TABS.map((tab) => {
            const isActive = trendPeriod === tab.key;
            return (
              <button
                key={tab.key}
                id={`trends-tab-${tab.key}`}
                role="tab"
                aria-selected={isActive}
                aria-controls={`trends-panel-${tab.key}`}
                onClick={() => setTrendPeriod(tab.key)}
                className="px-3 py-2 text-xs font-medium border-b-2 transition-colors whitespace-nowrap"
                style={{
                  minHeight: 44,
                  borderBottomColor: isActive ? COLORS.text.secondary : 'transparent',
                  color: isActive ? COLORS.text.secondary : COLORS.text.tertiary,
                }}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        {TREND_TABS.map((tab) => trendPeriod === tab.key && (
          <div
            key={tab.key}
            role="tabpanel"
            id={`trends-panel-${tab.key}`}
            aria-labelledby={`trends-tab-${tab.key}`}
            tabIndex={0}
            className="flex flex-col gap-2.5 pt-1"
          >
            {NET_CHANGE_GROUPS.map((group) => (
              <div key={group.group} className="flex flex-col gap-0.5">
                <div className="text-[10px] font-bold uppercase tracking-wide" style={{ color: COLORS.text.tertiary }}>
                  {group.group}
                </div>
                {group.rows.map((row) => {
                  const value =
                    tab.key === 'yesterday' ? row.yesterday(tc)
                    : tab.key === 'month' ? (curr != null ? row.thisMonth(curr) : null)
                    : (prev != null ? row.lastMonth(prev) : null);
                  return (
                    <div key={row.label} className="flex justify-between items-center text-xs">
                      <span style={{ color: COLORS.text.secondary }}>{row.label}</span>
                      <span className="font-mono" style={{ color: value != null ? COLORS.text.secondary : COLORS.text.tertiary }}>
                        {value != null ? formatNumber(Math.round(value)) : '—'}
                      </span>
                    </div>
                  );
                })}
                {(() => {
                  const netValue =
                    tab.key === 'yesterday' ? group.net.yesterday(tc)
                    : tab.key === 'month' ? (curr != null ? group.net.thisMonth(curr) : null)
                    : (prev != null ? group.net.lastMonth(prev) : null);
                  return (
                    <div className="flex justify-between items-center text-xs font-semibold">
                      <span style={{ color: COLORS.text.primary }}>{group.net.label}</span>
                      <span className="font-mono" style={{ color: netValue != null ? getNetChangeColor(netValue) : COLORS.text.tertiary }}>
                        {netValue != null ? formatNetValue(netValue) : '—'}
                      </span>
                    </div>
                  );
                })()}
              </div>
            ))}
          </div>
        ))}
      </GlanceCard>

      {/* ── Flow Breakdown ────────────────────────────────────── */}
      <div className="flex flex-col gap-2">
        <AccordionCard
          title="Gold Details"
          panelId="flow-gold"
          isOpen={expandedFlow === 'gold'}
          onToggle={() => setExpandedFlow((v) => (v === 'gold' ? null : 'gold'))}
        >
          {gold.rows.map((row, idx) => <MobileFlowRow key={idx} row={row} />)}
        </AccordionCard>
        <AccordionCard
          title="Food Details"
          panelId="flow-food"
          isOpen={expandedFlow === 'food'}
          onToggle={() => setExpandedFlow((v) => (v === 'food' ? null : 'food'))}
        >
          {food.rows.map((row, idx) => <MobileFlowRow key={idx} row={row} />)}
        </AccordionCard>
        <AccordionCard
          title="Runes Details"
          panelId="flow-runes"
          isOpen={expandedFlow === 'runes'}
          onToggle={() => setExpandedFlow((v) => (v === 'runes' ? null : 'runes'))}
        >
          {runes.rows.map((row, idx) => <MobileFlowRow key={idx} row={row} />)}
        </AccordionCard>
      </div>

      {/* ── Active Modifiers ──────────────────────────────────── */}
      <GlanceCard title="Active Modifiers">
        {modifiers.length === 0 ? (
          <div className="text-sm" style={{ color: COLORS.text.tertiary }}>No active modifiers.</div>
        ) : (
          modifiers.map((mod, idx) => (
            <ModifierRow key={idx} mod={mod} isLast={idx === modifiers.length - 1} />
          ))
        )}
      </GlanceCard>
    </div>
  );
}
